import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../App';

export default function EmailCompose() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ subject:'', body:'', cc:'', bcc:'' });
  const [selectedIds, setSelectedIds] = useState(() => {
    if (searchParams.get('to') === 'selected') {
      try { return JSON.parse(sessionStorage.getItem('cndb_email_recipients') || '[]'); } catch { return []; }
    }
    return [];
  });
  const [audience, setAudience] = useState(searchParams.get('to') === 'selected' ? 'selected' : 'all');
  const [filter, setFilter] = useState({ city:'', industry:'', tag:'' });
  const [opts, setOpts] = useState({ cities:[], industries:[], tags:[] });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    axios.get('/api/contacts/meta/filters').then(r => setOpts(r.data)).catch(()=>{});
    axios.get('/api/emails/templates').then(r => setTemplates(r.data)).catch(()=>{});
  }, []);

  const loadTemplate = id => {
    const t = templates.find(t => t.id == id);
    if (t) setForm(f => ({ ...f, subject: t.subject, body: t.body }));
  };

  const saveTemplate = async () => {
    if (!saveName || !form.subject || !form.body) return toast.error('Συμπληρώστε όλα τα πεδία');
    try {
      await axios.post('/api/emails/templates', { name: saveName, subject: form.subject, body: form.body });
      toast.success('Template αποθηκεύτηκε');
      setShowSave(false); setSaveName('');
      axios.get('/api/emails/templates').then(r => setTemplates(r.data));
    } catch { toast.error('Σφάλμα'); }
  };

  const handleSend = async () => {
    if (!form.subject || !form.body) return toast.error('Συμπληρώστε θέμα και περιεχόμενο');
    setLoading(true);
    try {
      const payload = {
        subject: form.subject,
        body: form.body,
        cc: form.cc ? form.cc.split(',').map(e=>e.trim()).filter(Boolean) : [],
        bcc: form.bcc ? form.bcc.split(',').map(e=>e.trim()).filter(Boolean) : [],
        filter: audience==='filter' ? filter : undefined,
        contact_ids: audience==='selected' ? selectedIds : undefined,
      };
      const { data } = await axios.post('/api/emails/send', payload);
      if (data.sent > 0) toast.success(`Στάλθηκε σε ${data.sent} επαφές${data.failed ? ` (${data.failed} απέτυχαν)` : ''}`);
      else toast.error(`Καμία αποστολή. ${data.error ? 'Αιτία: ' + data.error : ''}`, { duration: 8000 });
      setForm(f => ({ ...f, subject:'', body:'' }));
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα αποστολής'); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ maxWidth:'760px' }}>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={{ fontSize:'22px', fontWeight:700, letterSpacing:'-0.03em' }}>Αποστολή Email</h1>
        <p style={{ color:'var(--slate)', fontSize:'13px', marginTop:'4px' }}>Μαζική αποστολή στις επαφές του DataBank</p>
      </div>

      {/* Sender & Template */}
      <div className="card card-p" style={{ marginBottom:'14px' }}>
        <h3 style={{ fontSize:'13px', fontWeight:600, marginBottom:'14px', color:'var(--slate)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Αποστολέας</h3>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <div style={{ width:'40px', height:'40px', borderRadius:'50%', background:'linear-gradient(135deg,#0A0F1E,#1B4FD8)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'15px', fontWeight:700, color:'#fff', fontFamily:'Inter,sans-serif' }}>
              {(user.full_name||'A')[0]}
            </div>
            <div>
              <div style={{ fontWeight:600, fontSize:'14px' }}>{user.full_name} / {user.full_name_en}</div>
              <div style={{ fontSize:'12px', color:'var(--slate)' }}>{user.title_el} · <strong>{user.sender_email}</strong></div>
            </div>
          </div>
          <div className="form-group" style={{ margin:0, minWidth:'200px' }}>
            <label className="form-label">Template (προαιρετικό)</label>
            <select className="input input-sm" onChange={e => loadTemplate(e.target.value)} defaultValue="">
              <option value="">— Επιλέξτε template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Θέμα Email *</label>
          <input className="input" placeholder="Θέμα..." value={form.subject} onChange={e => set('subject', e.target.value)} />
        </div>
        <div className="form-row" style={{ marginBottom:0 }}>
          <div className="form-group" style={{ marginBottom:0 }}>
            <label className="form-label">CC (προαιρετικό)</label>
            <input className="input input-sm" placeholder="email1@example.com, email2@example.com" value={form.cc} onChange={e => set('cc', e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom:0 }}>
            <label className="form-label">BCC (προαιρετικό)</label>
            <input className="input input-sm" placeholder="email1@example.com, email2@example.com" value={form.bcc} onChange={e => set('bcc', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Audience */}
      <div className="card card-p" style={{ marginBottom:'14px' }}>
        <h3 style={{ fontSize:'13px', fontWeight:600, marginBottom:'12px', color:'var(--slate)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Παραλήπτες</h3>
        <div style={{ display:'flex', gap:'8px', marginBottom: audience==='filter' ? '14px' : 0, flexWrap:'wrap' }}>
          {[['all','Όλες οι ενεργές επαφές'],['filter','Με φίλτρο'],...(selectedIds.length ? [['selected',`Επιλεγμένες (${selectedIds.length})`]] : [])].map(([v,l]) => (
            <button key={v} className={`btn btn-sm ${audience===v ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setAudience(v)}>{l}</button>
          ))}
        </div>
        {audience==='selected' && (
          <p style={{ fontSize:'12px', color:'var(--slate)', margin:0 }}>Το email θα σταλεί στις {selectedIds.length} επαφές που επέλεξες στη σελίδα Επαφών.</p>
        )}
        {audience==='filter' && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px' }}>
            {[['city','Πόλη',opts.cities],['industry','Κλάδος',opts.industries],['tag','Ετικέτα',opts.tags]].map(([k,l,options]) => (
              <div key={k}>
                <label className="form-label">{l}</label>
                <select className="input input-sm" value={filter[k]} onChange={e=>setFilter(f=>({...f,[k]:e.target.value}))}>
                  <option value="">Όλα</option>
                  {options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="card card-p" style={{ marginBottom:'14px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
          <h3 style={{ fontSize:'13px', fontWeight:600, color:'var(--slate)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Περιεχόμενο</h3>
          <div style={{ display:'flex', gap:'6px' }}>
            <button className={`btn btn-ghost btn-sm ${preview ? 'btn-primary' : ''}`} onClick={() => setPreview(!preview)}>
              {preview ? 'Επεξεργασία' : 'Preview'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowSave(!showSave)}>Αποθήκευση ως Template</button>
          </div>
        </div>
        <p style={{ fontSize:'11px', color:'var(--mist)', marginBottom:'8px' }}>
          Μεταβλητές: <code style={{ background:'var(--fog)', padding:'1px 5px', borderRadius:'4px' }}>{'{{first_name}}'}</code>{' '}
          <code style={{ background:'var(--fog)', padding:'1px 5px', borderRadius:'4px' }}>{'{{last_name}}'}</code>{' '}
          <code style={{ background:'var(--fog)', padding:'1px 5px', borderRadius:'4px' }}>{'{{email}}'}</code>
        </p>
        {preview ? (
          <div style={{ border:'1px solid var(--silver)', borderRadius:'8px', padding:'16px', minHeight:'240px', fontSize:'14px', lineHeight:'1.6' }}
            dangerouslySetInnerHTML={{ __html: form.body || '<em style="color:#CBD5E1">Κενό περιεχόμενο</em>' }} />
        ) : (
          <textarea className="input" rows={14} style={{ fontFamily:'monospace', fontSize:'12px', lineHeight:'1.65' }}
            placeholder={'Γράψτε το περιεχόμενο. Υποστηρίζεται HTML.\n\nΠαράδειγμα:\n<h2>Γεια σας {{first_name}},</h2>\n<p>Θέλαμε να σας ενημερώσουμε για...</p>'}
            value={form.body} onChange={e => set('body', e.target.value)} />
        )}
        {showSave && (
          <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--silver)', display:'flex', gap:'8px', alignItems:'center' }}>
            <input className="input input-sm" style={{ flex:1 }} placeholder="Όνομα template..." value={saveName} onChange={e => setSaveName(e.target.value)} />
            <button className="btn btn-primary btn-sm" onClick={saveTemplate}>Αποθήκευση</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowSave(false)}>Ακύρωση</button>
          </div>
        )}
      </div>

      <button className="btn btn-gold btn-lg" onClick={handleSend} disabled={loading} style={{ gap:'8px' }}>
        {loading ? <><span className="spin" style={{ width:'15px', height:'15px', borderTopColor:'#fff', borderColor:'rgba(255,255,255,0.3)' }} />Αποστολή...</> : <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Αποστολή Email
        </>}
      </button>
    </div>
  );
}
